import { Loader2, Music, Upload } from 'lucide-react';
import { useState } from 'react';
import type { Control } from 'react-hook-form';

import type { Performance } from '~entities/recital-manager/model/recital';
import { getAllStudentsWithSearchAPI } from '~entities/student/api/get-all-students-with-search';

import MultipleSelector from '~shared/shadcn/expansions/multiple-selector';
import { Button } from '~shared/shadcn/ui/button';
import { Card, CardContent } from '~shared/shadcn/ui/card';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~shared/shadcn/ui/form';
import { Input } from '~shared/shadcn/ui/input';
import { Textarea } from '~shared/shadcn/ui/textarea';

import type { PerformanceFormValues } from './edit-performance-sheet';
import { UploadMusicFile } from './upload-music-file';

interface BasicInfoTabProps {
  control: Control<PerformanceFormValues>;
  actId: string;
  performance: Performance;
}

export const BasicInfoTab = ({
  control,
  actId,
  performance,
}: BasicInfoTabProps) => {
  const [uploadMusicDialogOpen, setUploadMusicDialogOpen] = useState(false);

  return (
    <div className='space-y-4'>
      <FormField
        control={control}
        name='name'
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Performance Title <span className='text-red-500'>*</span>
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name='duration'
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Duration (minutes) <span className='text-red-500'>*</span>
            </FormLabel>
            <FormControl>
              <Input type='number' min='1' {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Music File Upload Section */}
      <Card className='rounded-lg border'>
        <CardContent className='p-4'>
          <div className='mb-2'>
            <div className='flex items-center gap-2'>
              <Music className='size-5 text-yellow-500' />
              <h3 className='font-medium'>Music File</h3>
            </div>
          </div>

          {/* Audio element for playback with controls */}
          {performance.musicFile?.url ? (
            <div className='mb-2'>
              <audio
                src={performance.musicFile.url}
                preload='metadata'
                controls
                className='w-full'
              />
              <p className='mt-1 text-xs text-muted-foreground'>
                Tip: Right-click on the player and select &quot;Save audio
                as...&quot; for a direct download
              </p>
            </div>
          ) : (
            <p className='text-sm text-muted-foreground'>
              No music file uploaded
            </p>
          )}

          <Button
            type='button'
            className='w-full bg-yellow-400 py-6 text-black hover:bg-yellow-500'
            onClick={() => {
              setUploadMusicDialogOpen(true);
            }}
          >
            <Upload className='mr-2 size-5' />
            {performance.musicFile?.url
              ? 'Replace Music File'
              : 'Upload Music File'}
          </Button>
        </CardContent>
      </Card>

      {/* Music Upload Dialog */}
      <UploadMusicFile
        actId={actId}
        open={uploadMusicDialogOpen}
        setOpen={setUploadMusicDialogOpen}
        performance={performance}
      />

      <FormField
        control={control}
        name='studentIds'
        render={({ field }) => (
          <FormItem>
            <FormLabel>Students</FormLabel>
            <FormControl>
              <MultipleSelector
                onSearch={async (value) => {
                  const res = await getAllStudentsWithSearchAPI({
                    name: value,
                  });
                  return res.data.students.map((student) => ({
                    label: `${student.firstName} ${student.lastName}`,
                    value: student._id,
                  }));
                }}
                value={field.value}
                onChange={field.onChange}
                triggerSearchOnFocus
                placeholder='Select Students'
                delay={500}
                loadingIndicator={
                  <div className='flex items-center justify-center py-2'>
                    <Loader2 className='animate-spin' />
                  </div>
                }
                emptyIndicator={
                  <p className='w-full text-center text-lg leading-10 text-muted-foreground'>
                    no results found.
                  </p>
                }
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name='notes'
        render={({ field }) => (
          <FormItem>
            <FormLabel>Performance Notes</FormLabel>
            <FormControl>
              <Textarea {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
